import {
  PRICE_SCENARIOS,
  getIntervalType,
  isOneTimePrice,
} from "@/react/helpers/paywallPriceHelpers";
import type { Paywall } from "@/react/types/Paywall";
import type {
  PaywallPrice,
  PaywallPriceScenario,
} from "@/react/types/PaywallPrice";
import type { PaywallPriceForm } from "../../types/PaywallPriceForm";

const handleStringNumber = (input: string | number | undefined) =>
  Number(input) || 0;

type Validator = (
  price: PaywallPrice,
  paywall: Paywall,
) => null | PaywallPriceScenario;

const validateScenarioIsValue = (price: PaywallPrice, paywall: Paywall) => {
  if (isOneTimePrice(price)) {
    return PRICE_SCENARIOS.value;
  }

  if (handleStringNumber(paywall.trial_days) > 0) {
    return null;
  }

  if (handleStringNumber(price.upfront_payment_amount) > 0) {
    return null;
  }

  return PRICE_SCENARIOS.value;
};

const validateScenarioIsTrialThenValue = (
  price: PaywallPrice,
  paywall: Paywall,
) => {
  if (
    handleStringNumber(paywall.trial_days) > 0 &&
    handleStringNumber(price.upfront_payment_amount) === 0
  ) {
    return PRICE_SCENARIOS.trialThenValue;
  }

  return null;
};

const validateScenarioIsTrialThenUpfrontThenValue = (
  price: PaywallPrice,
  paywall: Paywall,
) => {
  if (
    handleStringNumber(paywall.trial_days) > 0 &&
    handleStringNumber(price.upfront_payment_amount) > 0 &&
    !price.trial_requires_upfront_payment
  ) {
    return PRICE_SCENARIOS.trialThenUpfrontThenValue;
  }

  return null;
};

const validateScenarioIsUpfrontThenValue = (
  price: PaywallPrice,
  paywall: Paywall,
) => {
  if (
    handleStringNumber(paywall.trial_days) === 0 &&
    handleStringNumber(price.upfront_payment_amount) > 0
  ) {
    return PRICE_SCENARIOS.upfrontThenValue;
  }

  return null;
};

const validateScenarioIsUpfrontThenTrialThenValue = (
  price: PaywallPrice,
  paywall: Paywall,
) => {
  if (
    handleStringNumber(paywall.trial_days) > 0 &&
    handleStringNumber(price.upfront_payment_amount) > 0 &&
    price.trial_requires_upfront_payment
  ) {
    return PRICE_SCENARIOS.upfrontThenTrialThenValue;
  }

  return null;
};

export const buildScenariosStrategies: Validator[] = [
  validateScenarioIsValue,
  validateScenarioIsTrialThenValue,
  validateScenarioIsTrialThenUpfrontThenValue,
  validateScenarioIsUpfrontThenValue,
  validateScenarioIsUpfrontThenTrialThenValue,
];

export const generatePriceWithBackendVariables = (
  price: PaywallPriceForm,
  paywall: Paywall,
): PaywallPrice | null => {
  if (price._destroy) {
    return null;
  }

  return {
    ...price,
    // eslint-disable-next-line sonarjs/pseudo-random -- Disabled for plugin version update. Fix when possible
    id: price?.id || Math.floor(Math.random() * 100000),
    scenario:
      buildScenariosStrategies.reduce(
        (acc: PaywallPriceScenario | null, validator: Validator) => {
          if (acc) return acc;
          return validator(price, paywall);
        },
        null,
      ) || "value",
    interval_type: getIntervalType(price),
    payable_payment_methods: ["card"],
    trial_days: Number(paywall.trial_days),
  };
};
